/**
 * Section-agnostic data hook for the unified entity performance report.
 *
 * `useReport` fetches a fully resolved, render-complete report for an asset,
 * hybrid, system, or installation via the unified `getReport` GraphQL query.
 * The heavy lifting (spec resolution, capability intersection, aggregation,
 * reducers/calculators, availability gating) all happens server-side — this
 * hook only computes a sensible default window, maps the caller's scope to the
 * generated `ReportScope` enum, and hands the query result back in a clean,
 * typed shape.
 *
 * The query document (`GetReportDocument`) is a generated typed-document node
 * imported from `@hydrologiq/gql-experience/graphql` — there is no inline `gql`
 * tag (workspace rule). It is fetched through the "primary" (experience)
 * endpoint, mirroring the calendar / forecast hooks.
 *
 * Requirements: 10.2 (shared, section-agnostic hook), 10.6 (7-day default
 * window + default frequency when the caller omits bounds).
 */
import { useMemo } from "react"
import { endOfDay, startOfDay, subDays } from "date-fns"
import { GetPublicReportDocument, GetReportDocument, ReportScope } from "@hydrologiq/gql-experience/graphql"
import type {
  GetPublicReportQuery,
  GetPublicReportQueryVariables,
  GetReportQuery,
  GetReportQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { DEFAULT_FREQUENCY, type Frequency } from "./frequency"

/**
 * Number of days covered by the default window when the caller does not supply
 * an explicit `start` / `stop`.
 */
const DEFAULT_WINDOW_DAYS = 7

/** Lowercase scope literals callers may pass instead of the generated enum. */
export type ReportScopeString = "asset" | "hybrid" | "system" | "installation"

/**
 * The scope a report can target. Callers may pass either the generated
 * `ReportScope` enum or the equivalent lowercase string literal; both resolve
 * to the same enum value sent to the backend.
 */
export type ReportScopeInput = ReportScope | ReportScopeString

/**
 * The resolved report as returned by the `getReport` query (the narrowed query
 * result shape — KPIs, graphs, columns, rows, plus availability). Derived from
 * the generated query type so it stays in lockstep with the document selection.
 */
export type Report = GetReportQuery["getReport"]

/**
 * A single KPI as returned by the report (narrowed from the generated query
 * type). Each KPI may carry an optional {@link ReportKpiTrend}.
 */
export type ReportKpi = Report["kpis"][number]

/**
 * The per-KPI trend indicator: the signed change (`delta`) over the preceding
 * window, an optional percentage change (`percent`), and a `direction`
 * discriminator ("up" | "down" | "flat"). `null` when the preceding window has
 * no comparable data — consumers should render no arrow in that case
 * (Requirement 6.5). Derived from the generated query type so it tracks the
 * document's `trend { delta percent direction }` selection.
 */
export type ReportKpiTrend = NonNullable<ReportKpi["trend"]>

/**
 * The reporting-window bounds the report was aggregated over, as ISO 8601
 * strings. Exposed so the document period block can render the selected period
 * and reflect changes to it (Requirement 7.4).
 */
export interface ReportWindow {
  /** ISO 8601 window start (inclusive). */
  start: string
  /** ISO 8601 window end (exclusive/now). */
  stop: string
}

export interface UseReportResult {
  /** The resolved, render-complete report, or `undefined` while first loading. */
  report?: Report
  /**
   * The reporting-window bounds the report covers (the caller's `start`/`stop`
   * when supplied, otherwise the computed default window). Feeds the document
   * period block (Requirement 7.4).
   */
  window: ReportWindow
  /** True while the query is in flight. */
  loading: boolean
  /** A normalised `Error` if the query failed, otherwise `undefined`. */
  error?: Error
  /** Re-run the query. */
  refetch: () => void
}

/**
 * Map a lowercase scope string to the generated `ReportScope` enum value.
 *
 * The generated enum's *values* are the uppercase server tokens
 * (`ReportScope.Asset === "ASSET"`), so a lowercase caller string cannot be
 * used directly — it must be looked up here.
 */
const SCOPE_BY_STRING: Record<ReportScopeString, ReportScope> = {
  asset: ReportScope.Asset,
  hybrid: ReportScope.Hybrid,
  system: ReportScope.System,
  installation: ReportScope.Installation,
}

/**
 * Normalise a scope argument (enum value or lowercase string) to the generated
 * `ReportScope` enum value the `getReport` variables expect.
 *
 * `ReportScope` is a string enum, so its members are assignable to `string`;
 * we therefore probe the lowercase lookup map by key rather than relying on a
 * `typeof` narrow (which cannot distinguish an enum value from a plain string).
 */
export function toReportScope(scope: ReportScopeInput): ReportScope {
  return (SCOPE_BY_STRING as Record<string, ReportScope | undefined>)[scope] ?? (scope as ReportScope)
}

/**
 * Resolve the reporting window, anchored to `stop`.
 *
 * The window is always a `DEFAULT_WINDOW_DAYS`-day span **ending at `stop`**:
 * `start` is derived as `stop − DEFAULT_WINDOW_DAYS`, not from "now". This makes
 * week-by-week lookback trivial — a caller only moves `stop` back and the whole
 * window shifts with it (rather than the span stretching from a fixed "now").
 *
 * Both bounds are snapped to day edges so the window is a whole number of full
 * days aligned to daily buckets: `stop` is the **end of its day** (23:59:59.999
 * local) and the derived `start` is the **start of a day**. The span is exactly
 * `DEFAULT_WINDOW_DAYS` full days *ending on the stop day* — i.e. the stop day
 * plus the `DEFAULT_WINDOW_DAYS − 1` preceding days — so a 7-day window covers 7
 * daily buckets, not 8. When `stop` is omitted it defaults to end of today
 * (→ the latest week). An explicit `start` is honoured as-is.
 *
 * Bounds are returned as ISO 8601 strings (trailing `Z`), which the backend's
 * `parse_iso_timestamptz` accepts directly.
 */
function resolveWindow(start?: string, stop?: string): { start: string; stop: string } {
  const stopDate = endOfDay(stop ? new Date(stop) : new Date())
  return {
    start: start ?? startOfDay(subDays(stopDate, DEFAULT_WINDOW_DAYS - 1)).toISOString(),
    stop: stopDate.toISOString(),
  }
}

/**
 * Fetch a unified entity performance report.
 *
 * @param scope     Entity kind the report targets (enum or lowercase string).
 * @param entityId  Identifier of the entity (asset / system / installation).
 * @param start     Optional ISO 8601 window start. Defaults to the start of the
 *                  day `DEFAULT_WINDOW_DAYS − 1` days before `stop`'s day (a
 *                  whole `DEFAULT_WINDOW_DAYS`-day window ending on the stop day).
 * @param stop      Optional ISO 8601 window end. Normalised to end of that day;
 *                  defaults to end of today. The window anchors to `stop`.
 * @param frequency Optional download granularity. Defaults to {@link DEFAULT_FREQUENCY}.
 * @param shareToken Optional opaque share token. When supplied the hook fetches
 *                  the login-free `getPublicReport` query (keyed by the token,
 *                  which the backend resolves to its granted scope/entity) in
 *                  place of the authed `getReport` — the public share view
 *                  (`PublicReportView`) sets this. `scope`/`entityId` are then
 *                  ignored by the backend but still select the report shape.
 */
export function useReport(
  scope: ReportScopeInput,
  entityId: string,
  start?: string,
  stop?: string,
  frequency: Frequency = DEFAULT_FREQUENCY,
  shareToken?: string,
): UseReportResult {
  // Build the query variables once per (scope, entityId, start, stop, frequency)
  // input. Crucially, the default-window fallback calls `new Date()`, which must
  // NOT run on every render: an unmemoised default window would produce a new
  // `stop` ISO string each render, so Apollo would see new variables, refetch,
  // re-render, and loop forever. Memoising on the explicit inputs pins the
  // default window to the first render (stable until the caller's inputs change).
  //
  // The window is anchored to `stop` (see resolveWindow): `stop` defaults to end
  // of today, and a missing `start` is derived as `stop − DEFAULT_WINDOW_DAYS`
  // rather than from "now". So a caller that moves `stop` back for lookback gets
  // the whole 7-day window shifted with it, not a span stretching to now. An
  // explicit `start` is still honoured as-is.
  // The resolved window is the same for both transports — it anchors to `stop`
  // and feeds back the exposed period (Requirement 7.4). Memoised on the
  // explicit inputs so the default-window `new Date()` is pinned to the first
  // render (an unmemoised default would churn `stop` and loop forever).
  const window = useMemo<ReportWindow>(() => resolveWindow(start, stop), [start, stop])

  // Public share view: fetch `getPublicReport` keyed by the opaque token. The
  // backend resolves scope/entity/tenant from the token's grant and ignores any
  // client scope/entityId, so those are omitted here. There is no `tz` arg on
  // the public query (the resolver clamps/aggregates server-side).
  const publicVariables = useMemo<GetPublicReportQueryVariables>(
    () => ({
      shareToken: shareToken ?? "",
      start: window.start,
      stop: window.stop,
      frequency,
    }),
    [shareToken, window.start, window.stop, frequency],
  )

  // Authed view: fetch `getReport` for the caller's (scope, entityId). The IANA
  // zone must match the local day edges `resolveWindow` snapped to — without it
  // the backend defaults to UTC, offsetting daily buckets by an hour and making
  // the on-screen values disagree with the download.
  const reportVariables = useMemo<GetReportQueryVariables>(
    () => ({
      scope: toReportScope(scope),
      entityId,
      start: window.start,
      stop: window.stop,
      frequency,
      tz: IANATimeZone(),
    }),
    [scope, entityId, window.start, window.stop, frequency],
  )

  // Select the transport by whether a share token was supplied. Both resolvers
  // return the identical `ReportResponse` shape, so the result is read through a
  // single `GetReportQuery`-typed query (the public response is structurally
  // assignable). The document is accepted untyped by `useQueryWithEndpoint`.
  const isShare = Boolean(shareToken)
  const { data, loading, error, refetch } = useQueryWithEndpoint<GetReportQuery & GetPublicReportQuery>(
    isShare ? GetPublicReportDocument : GetReportDocument,
    {
      variables: isShare ? publicVariables : reportVariables,
      skip: isShare ? !shareToken : !entityId,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    "primary",
  )

  return {
    report: (isShare ? data?.getPublicReport : data?.getReport) ?? undefined,
    window,
    loading,
    error: error ? new Error(error.message) : undefined,
    refetch,
  }
}

export default useReport
